// Overlay for editing line-family elements (straight line / folded polyline /
// arced path). Renders:
//   • a square handle on every anchor node (drag to reshape),
//   • a round "bend" handle at every segment midpoint (drag to arc that
//     segment; drag back to the midpoint to straighten it), and
//   • a dashed angle reference line from the chord midpoint to the bend handle
//     while a segment is curved.
import React, { useRef } from "react";
import {
  parseLineModel,
  serializeLineModel,
  segHandle,
  midpoint,
  moveNode,
  setSegCurve,
} from "./lineEdit";

export default function LineEditOverlay({
  element,
  scale = 1,
  getSvgPoint,
  onLiveUpdate,
  onResizeStart,
  onResizeEnd,
}) {
  const dragRef = useRef(null);
  const model = parseLineModel(element);
  const { nodes, curves } = model;

  const handleSize = 9 / scale;
  const dotR = 5.5 / scale;
  const stroke = 1.5 / scale;

  const beginDrag = (e, apply) => {
    e.stopPropagation();
    e.preventDefault();
    onResizeStart?.();
    e.target.setPointerCapture?.(e.pointerId);
    const base = parseLineModel(element);
    dragRef.current = true;
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const p = getSvgPoint(ev.clientX, ev.clientY);
      const nextModel = apply(base, p);
      const patch = serializeLineModel(nextModel, element);
      onLiveUpdate(element.id, patch.attrs, patch.type);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onResizeEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <g pointerEvents="auto" data-testid="line-edit-overlay">
      {/* Angle reference lines for curved segments */}
      {curves.map((cv, i) => {
        if (!cv) return null;
        const mid = midpoint(nodes[i], nodes[i + 1]);
        return (
          <line
            key={`ref-${i}`}
            x1={mid.x}
            y1={mid.y}
            x2={cv.hx}
            y2={cv.hy}
            stroke="#FF6B00"
            strokeWidth={stroke}
            strokeDasharray={`${4 / scale} ${3 / scale}`}
            opacity={0.8}
            data-testid={`line-arc-ref-${i}`}
          />
        );
      })}

      {/* Bend handles (round) at each segment midpoint */}
      {curves.map((cv, i) => {
        const h = segHandle(model, i);
        return (
          <circle
            key={`bend-${i}`}
            cx={h.x}
            cy={h.y}
            r={dotR}
            fill={cv ? "#FF6B00" : "#0a0a0a"}
            stroke="#FF6B00"
            strokeWidth={stroke}
            style={{ cursor: "move" }}
            onPointerDown={(e) => beginDrag(e, (base, p) => setSegCurve(base, i, p, 6 / scale))}
            data-testid={`line-bend-handle-${i}`}
          />
        );
      })}

      {/* Anchor node handles (square) */}
      {nodes.map((n, i) => (
        <rect
          key={`node-${i}`}
          x={n.x - handleSize / 2}
          y={n.y - handleSize / 2}
          width={handleSize}
          height={handleSize}
          fill="#fff"
          stroke="#FF6B00"
          strokeWidth={stroke}
          style={{ cursor: "move" }}
          onPointerDown={(e) => beginDrag(e, (base, p) => moveNode(base, i, p))}
          data-testid={`line-node-handle-${i}`}
        />
      ))}
    </g>
  );
}
